import { z } from 'zod';
import { CargoExchangeRateSchema, CargoNonnegMoneySchema } from './article.js';
import { CargoPaymentCurrencySchema } from './enums.js';

// Cargo client-payment workflow request DTOs (#1926 Workstream B): the
// manager-submit body and the financier-review decision batch. These are the
// INPUT contracts the manager/financier endpoints accept; the resulting wire
// projection lives on `CargoPaymentDocumentWireSchema` (payments.ts) and the
// list cells on `CargoArticleWireSchema` (registry.ts).

/**
 * @aiPurpose manager-submit body: the desired client-payment status the Manager
 *   requests for review (#1926 Workstream B). Only the two requestable values.
 */
export const CargoClientPaymentSubmitBodySchema = z
  .object({ requestedStatus: z.enum(['paid', 'partially_paid']) })
  .strict();
export type CargoClientPaymentSubmitBody = z.infer<typeof CargoClientPaymentSubmitBodySchema>;

/**
 * @aiPurpose one financier review decision for a single payment document (#1926
 *   Workstream B). The currency/money fields are strictly conditional on the
 *   decision and currency (see superRefine); a forbidden present field is a
 *   validation ERROR, never silently dropped.
 * @aiInvariant `decision === 'rejected'` carries NO money fields. An `accepted`
 *   decision requires a currency; `RUB` requires `receivedRub` (no
 *   foreign/rate); `USD`/`CNY` require `foreignAmount` + `exchangeRate` and
 *   forbid client-supplied `receivedRub` (the backend computes it).
 */
export const CargoPaymentReviewDecisionSchema = z
  .object({
    documentId: z.string().uuid(),
    decision: z.enum(['accepted', 'rejected']),
    currency: CargoPaymentCurrencySchema.optional(),
    foreignAmount: CargoNonnegMoneySchema.optional(),
    exchangeRate: CargoExchangeRateSchema.optional(),
    receivedRub: CargoNonnegMoneySchema.optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.decision === 'rejected') {
      for (const field of ['currency', 'foreignAmount', 'exchangeRate', 'receivedRub'] as const) {
        if (val[field] !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'a rejected decision must not carry payment fields',
            path: [field],
          });
        }
      }
      return;
    }

    // decision === 'accepted'
    if (val.currency === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'accepted decision requires a currency',
        path: ['currency'],
      });
      return;
    }

    if (val.currency === 'RUB') {
      if (val.receivedRub === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'a RUB accepted decision requires receivedRub',
          path: ['receivedRub'],
        });
      }
      if (val.foreignAmount !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'a RUB accepted decision must not carry foreignAmount',
          path: ['foreignAmount'],
        });
      }
      if (val.exchangeRate !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'a RUB accepted decision must not carry exchangeRate',
          path: ['exchangeRate'],
        });
      }
      return;
    }

    // currency === 'USD' | 'CNY'
    if (val.foreignAmount === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'a foreign-currency accepted decision requires foreignAmount',
        path: ['foreignAmount'],
      });
    }
    if (val.exchangeRate === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'a foreign-currency accepted decision requires exchangeRate',
        path: ['exchangeRate'],
      });
    }
    if (val.receivedRub !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'receivedRub is backend-computed for a foreign-currency decision',
        path: ['receivedRub'],
      });
    }
  });
export type CargoPaymentReviewDecision = z.infer<typeof CargoPaymentReviewDecisionSchema>;

/**
 * @aiPurpose financier-review batch body (#1926 Workstream B): one or more
 *   per-document decisions.
 */
export const CargoClientPaymentReviewBodySchema = z
  .object({
    // WHY (epic Decision 15): an unchanged document is OMITTED from `decisions`
    // — it is never sent as a 'pending' decision, so the array carries only
    // accept/reject actions the financier actually took.
    decisions: z.array(CargoPaymentReviewDecisionSchema).min(1),
  })
  .strict();
export type CargoClientPaymentReviewBody = z.infer<typeof CargoClientPaymentReviewBodySchema>;
